import React, { useState } from "react";
import MobileTableRelatedIndicators from "./MobileTableRelatedIndicators";
import CustomTable from "../../Tables/CustomTable";
import Pagination from "../../Pagination/Pagination";
import LinkUI from "../../Buttons/LinkUI";
import {
  Columns,
  Indicator,
  OdsPriorityGroups,
} from "../../../interfaces/ODSPriorityGroups";
import { Dimensions } from "../../../interfaces/Rights";
import EvaluationStatesCircle from "../../States/EvaluationStatesCircle";
import { getSortedIndicators } from "../../../utils/sorted";
import { GROUPS, INDICATOR_TYPE_STRUCTURAL } from "../../../utils/const";
import EvaluationLaws from "../../States/EvaluationLaws";
import { buildIndicatorUrl, onClickLinkUI } from "./utils";

interface TableProps {
  indicadores: Indicator[];
  period: string;
  pagination: {
    currentPage: number;
    pageSize: number;
  };
  handlePageChange: (page: number) => void;
  odsSelected: OdsPriorityGroups;
  module: string;
}

const Tables = ({
  indicadores,
  period,
  pagination,
  handlePageChange,
  odsSelected,
  module,
}: TableProps) => {
  const indicadoresPaginados =
    indicadores?.slice(
      (pagination?.currentPage - 1) * pagination?.pageSize,
      pagination?.currentPage * pagination?.pageSize
    ) ?? [];

  const totalPages = Math.ceil(
    (indicadores ?? []).length / pagination?.pageSize
  );

  const [sortConfig, setSortConfig] = useState<{
    key: keyof Indicator | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });

  const columns: Columns[] = [
    {
      label: "Indicadores",
      accessor: "titulo",
      render: (value: string, row: Indicator) => {
        const indicador = row;

        return (
          <LinkUI
            href={buildIndicatorUrl(
              indicador,
              { id: odsSelected.id, name: odsSelected.nombre },
              module,
              period,
              GROUPS
            )}
            onClick={() => onClickLinkUI(indicador, { id: odsSelected.id, name: odsSelected.nombre }, module, period, GROUPS, pagination.currentPage)}
            title={value}
          >
            {value}
          </LinkUI>
        );
      },
    },
    {
      label: "Derecho",
      accessor: "derecho",
      sortable: true,
      getValue: (row) => row.derecho?.titulo ?? "",
      render: (_value, row) => (
        <span className="text-system-light-40 text-blarge">
          {row.derecho.titulo}
        </span>
      ),
    },
    {
      label: "Dimensión",
      accessor: "dimension",
      getValue: (row) => row.dimension?.titulo ?? "",
      sortable: true,
      render: (value: Dimensions) => (
        <span className="text-system-light-40 text-blarge">{value.titulo}</span>
      ),
    },
    ...(module === GROUPS
      ? [
        {
          label: "Subdimensión",
          accessor: "subdimension" as keyof Indicator,
          sortable: true,
          getValue: (row: Indicator) => row.subdimension?.titulo ?? "",
          render: (_value: string, row: Indicator) => (
            <span className="text-system-light-40 text-blarge">
              {row.subdimension?.titulo ?? "-"}
            </span>
          ),
        },
      ]
      : []),
    {
      label: "Evaluación",
      accessor: "evaluaciones",
      sortable: true,
      getValue: (row) => {
        const raw = row.evaluaciones?.[period];
        const num = Number(raw);
        return isNaN(num) ? null : num;
      },
      render: (_value, row) => {
        const tipo = row.tipoIndicador?.titulo;
        if (tipo !== INDICATOR_TYPE_STRUCTURAL) {
          return (
            <div className="flex justify-center text-blarge">
              <EvaluationStatesCircle
                status={
                  typeof row.evaluaciones?.[period] === "string" &&
                    !isNaN(Number(row.evaluaciones[period]))
                    ? Number(row.evaluaciones[period])
                    : typeof row.evaluaciones?.[period] === "number"
                      ? row.evaluaciones[period]
                      : null
                }
                size={12}
              />
            </div>
          );
        } else {
          const evaluation = row.evaluaciones?.[period];
          return (
            <div className="w-[140px] h-[56px] flex flex-col justify-center items-center">
              <EvaluationLaws
                evaluacion={evaluation !== null ? String(evaluation) : null}
              />
            </div>
          );
        }
      },
    },
  ];

  return (
    <div id='tables-ods-priority_group'>
      <div className="block md:hidden mt-8">
        <MobileTableRelatedIndicators
          indicadores={indicadoresPaginados}
          period={period}
          buildIndicatorUrl={buildIndicatorUrl}
          odsSelected={{ id: odsSelected.id, name: odsSelected.nombre }}
          module={module}
          page={pagination.currentPage}
        />
      </div>
      <div className="hidden md:block mt-8">
        <CustomTable
          bgColorTr="bg-brand-green-30 text-primary-100"
          columns={columns}
          data={getSortedIndicators(indicadoresPaginados, columns, sortConfig)}
          onSort={(key) => {
            setSortConfig((prev) => ({
              key,
              direction:
                prev.key === key && prev.direction === "asc" ? "desc" : "asc",
            }));
          }}
        />
      </div>
      <Pagination
        data={indicadoresPaginados || []}
        totalPages={totalPages}
        currentPage={pagination?.currentPage || 1}
        setCurrentPage={handlePageChange}
        scrollTargetId="tables-ods-priority_group"
        scrollDuration={100}
      />
    </div>
  );
};

export default Tables;
